import { Component, OnDestroy, OnInit } from '@angular/core';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { ImmoTableControllerRequestService } from './service/immo-table-controller-request.service';
import {
  ImmoTableHelper,
  ImmoTableProcessResult,
} from './helper/immo-table-helper';
import { of, Subject } from 'rxjs';
import { TourPlanningEventService } from '../service/tour-planning-event.service';
import { ImmoTableSubProject } from './helper/model/immo-table-sub-project';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import BrowserHelper from 'src/app/helpers/browser-helper';

@Component({
  selector: 'immo-table',
  templateUrl: './immo-table.component.html',
  styleUrls: ['./immo-table.component.scss'],
})
export class ImmoTableComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  private getSubProjectsSubject: Subject<any>;

  loading: boolean = true;

  baseProjects: ImmoTableSubProject[];
  adjustments: ImmoTableSubProject[];
  soldRenteds: ImmoTableSubProject[];
  repos: ImmoTableSubProject[];

  baseProjectsCollapsed: boolean = true;
  adjustmentsCollapsed: boolean = true;
  soldRentedsCollapsed: boolean = true;
  reposCollapsed: boolean = true;

  browserIsSafari: boolean;

  constructor(
    private immoTableControllerRequestService: ImmoTableControllerRequestService,
    private tourPlanningEventService: TourPlanningEventService
  ) {
    super();
  }

  ngOnInit(): void {
    this.browserIsSafari = BrowserHelper.isSafari();
    this.getSubProjectsSubject = new Subject<any>();

    this.addSubscription(
      this.getSubProjectsSubject.pipe(
        startWith(ImmoTableHelper.EMPTY_RESULT),
        switchMap(async () => {
          this.loading = true;
          const subProjects: ImmoTableSubProject[] =
            await this.immoTableControllerRequestService.subProjects();
          return ImmoTableHelper.processSubProjects(subProjects);
        }),
        catchError(() => {
          return of(ImmoTableHelper.EMPTY_RESULT);
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ImmoTableProcessResult) => {
        this.baseProjects = res.baseProjects;
        this.adjustments = res.adjustments;
        this.soldRenteds = res.soldRenteds;
        this.repos = res.repos;
        this.loading = false;
      }
    );
    this.addSubscription(
      this.tourPlanningEventService.getSubProjectWasAssignedSubject(),
      (subProjectId: number) => this.removeSubProject(subProjectId)
    );
    this.addSubscription(
      this.tourPlanningEventService.getSubProjectWasUnassignedSubject(),
      () => this.getSubProjectsSubject.next()
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getSubProjectsSubject != null) {
      this.getSubProjectsSubject.complete();
    }
  }

  private removeSubProject(subProjectId: number): void {
    let removed = this.tryRemoveSubProject(subProjectId, this.baseProjects);
    if (removed) {
      return;
    }
    removed = this.tryRemoveSubProject(subProjectId, this.adjustments);
    if (removed) {
      return;
    }
    removed = this.tryRemoveSubProject(subProjectId, this.soldRenteds);
    if (removed) {
      return;
    }
    this.tryRemoveSubProject(subProjectId, this.repos);
  }

  private tryRemoveSubProject(
    subProjectId: number,
    subProjects: ImmoTableSubProject[]
  ): boolean {
    if (!CollectionHelper.isPopulated(subProjects)) {
      return false;
    }

    const index = subProjects.findIndex((sp) => sp.id === subProjectId);
    if (index < 0) {
      return false;
    }

    subProjects.splice(index, 1);
    return true;
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}
